/**
 * <hauser-claim-modal> — Custom Element ESM (ha/534).
 *
 * Modal embarquable qui permet à un visiteur d'activer son compte Hauser
 * via un token de claim (purpose=claim, ha/533 AccessTokenService).
 * Soumission vers `/ha/form` avec `formId=hauser.claim.account`, CSRF inclus.
 *
 * Attributes :
 *   token        — jeton de claim (requis)
 *   role         — rôle à assigner, ex. `agency_client` (requis)
 *   redirect     — pageId de destination après activation (optionnel)
 *   account      — email du compte à afficher (optionnel, informatif)
 *   label-title  — titre du modal (défaut "Activez votre accès")
 *   label-sub    — sous-titre (défaut générique)
 *   label-submit — libellé du bouton (défaut "Activer mon compte")
 *
 * Événements émis :
 *   hauser-claim:success  — activation réussie ; detail = { response }
 *   hauser-claim:error    — erreur serveur ;  detail = { message }
 */

const CSS = `
:host {
  display: block;
}
.hcm-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,.55);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9000;
  padding: 16px;
}
.hcm-card {
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 8px 40px rgba(0,0,0,.18);
  padding: 32px;
  width: 100%;
  max-width: 420px;
  box-sizing: border-box;
}
.hcm-title {
  margin: 0 0 4px;
  font-size: 20px;
  font-weight: 700;
  color: #111;
}
.hcm-sub {
  margin: 0 0 20px;
  font-size: 13.5px;
  color: #666;
  line-height: 1.5;
}
.hcm-field { margin-bottom: 16px; }
.hcm-label {
  display: block;
  font-size: 13px;
  font-weight: 600;
  color: #333;
  margin-bottom: 6px;
}
.hcm-input {
  width: 100%;
  box-sizing: border-box;
  padding: 10px 12px;
  border: 1.5px solid #d0d0d0;
  border-radius: 8px;
  font-size: 14px;
  outline: none;
  transition: border-color .15s;
}
.hcm-input:focus { border-color: #1A3A52; }
.hcm-input.is-error { border-color: #c62828; }
.hcm-btn {
  width: 100%;
  padding: 12px;
  border: none;
  border-radius: 8px;
  background: #1A3A52;
  color: #fff;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: opacity .15s;
}
.hcm-btn:hover { opacity: .88; }
.hcm-btn:disabled { opacity: .5; cursor: default; }
.hcm-error {
  margin-top: 12px;
  padding: 10px 12px;
  background: #ffebee;
  border: 1px solid #ef9a9a;
  border-radius: 6px;
  font-size: 13px;
  color: #c62828;
}
.hcm-strength {
  display: flex;
  gap: 4px;
  margin-top: 6px;
}
.hcm-seg {
  flex: 1;
  height: 3px;
  border-radius: 2px;
  background: #e0e0e0;
  transition: background .2s;
}
.hcm-seg.weak   { background: #ef5350; }
.hcm-seg.medium { background: #ff9800; }
.hcm-seg.strong { background: #43a047; }
.hcm-rules { margin-top: 6px; }
.hcm-rule {
  font-size: 12px;
  color: #aaa;
  margin-bottom: 2px;
  display: flex;
  align-items: center;
  gap: 6px;
}
.hcm-rule.ok { color: #43a047; }
.hcm-rule::before {
  content: '○';
  font-size: 10px;
  flex-shrink: 0;
}
.hcm-rule.ok::before { content: '✓'; }
`;

const TEMPLATE = `
<div class="hcm-backdrop" part="backdrop">
  <div class="hcm-card" role="dialog" aria-modal="true" aria-labelledby="hcm-title">
    <h2 id="hcm-title" class="hcm-title">Activez votre accès</h2>
    <p class="hcm-sub">Créez votre mot de passe pour accéder à votre espace à tout moment.</p>
    <form class="hcm-form" novalidate autocomplete="off">
      <div class="hcm-field">
        <label class="hcm-label" for="hcm-pass">Mot de passe</label>
        <input id="hcm-pass" class="hcm-input" type="password"
               autocomplete="new-password" placeholder="Choisissez un mot de passe" required>
        <div class="hcm-strength">
          <div class="hcm-seg" data-seg="0"></div>
          <div class="hcm-seg" data-seg="1"></div>
          <div class="hcm-seg" data-seg="2"></div>
          <div class="hcm-seg" data-seg="3"></div>
        </div>
        <div class="hcm-rules">
          <div class="hcm-rule" data-rule="len">8 caractères minimum</div>
          <div class="hcm-rule" data-rule="upper">Une majuscule</div>
          <div class="hcm-rule" data-rule="num">Un chiffre ou symbole</div>
        </div>
      </div>
      <div class="hcm-field">
        <label class="hcm-label" for="hcm-confirm">Confirmer le mot de passe</label>
        <input id="hcm-confirm" class="hcm-input" type="password"
               autocomplete="new-password" placeholder="Répétez le mot de passe" required>
      </div>
      <button type="submit" class="hcm-btn">Activer mon compte</button>
      <div class="hcm-error" hidden role="alert"></div>
    </form>
  </div>
</div>
`;

export class HauserClaimModal extends HTMLElement {
    private _submitting = false;
    private _shadow: ShadowRoot;

    static get observedAttributes(): string[] {
        return ["token", "role", "redirect", "account", "label-title", "label-sub", "label-submit"];
    }

    constructor() {
        super();
        this._shadow = this.attachShadow({ mode: "open" });
        const style = document.createElement("style");
        style.textContent = CSS;
        this._shadow.appendChild(style);
    }

    connectedCallback(): void {
        this._shadow.innerHTML += TEMPLATE;
        this._applyLabels();
        this._wireStrength();
        this._wireSubmit();
        queueMicrotask(() => {
            const pass = this._shadow.querySelector<HTMLInputElement>("#hcm-pass");
            pass?.focus();
        });
    }

    private _applyLabels(): void {
        const title   = this.getAttribute("label-title");
        const sub     = this.getAttribute("label-sub");
        const submit  = this.getAttribute("label-submit");
        const account = this.getAttribute("account");

        if (title) {
            const el = this._shadow.querySelector<HTMLElement>("#hcm-title");
            if (el !== null) el.textContent = title;
        }
        if (sub || account) {
            const el = this._shadow.querySelector<HTMLElement>(".hcm-sub");
            if (el !== null) {
                el.textContent = sub ?? `Créez votre mot de passe pour accéder au compte ${account ?? ""}.`;
            }
        }
        if (submit) {
            const btn = this._shadow.querySelector<HTMLElement>(".hcm-btn");
            if (btn !== null) btn.textContent = submit;
        }
    }

    private _wireStrength(): void {
        const pass = this._shadow.querySelector<HTMLInputElement>("#hcm-pass");
        if (pass === null) return;
        pass.addEventListener("input", () => this._updateStrength(pass.value));
    }

    private _updateStrength(value: string): void {
        const len   = value.length >= 8;
        const upper = /[A-Z]/.test(value);
        const num   = /[\d!@#$%^&*]/.test(value);

        const rules = this._shadow.querySelectorAll<HTMLElement>("[data-rule]");
        rules.forEach(r => {
            const key = r.dataset["rule"];
            const ok  = key === "len" ? len : key === "upper" ? upper : num;
            r.classList.toggle("ok", ok);
        });

        const score = [len, upper, num].filter(Boolean).length + (value.length >= 12 ? 1 : 0);
        const level = score <= 1 ? "weak" : score <= 2 ? "weak" : score === 3 ? "medium" : "strong";

        const segs = this._shadow.querySelectorAll<HTMLElement>("[data-seg]");
        segs.forEach((seg, i) => {
            seg.className = "hcm-seg";
            if (score > 0 && i < score) seg.classList.add(level);
        });
    }

    private _wireSubmit(): void {
        const form = this._shadow.querySelector<HTMLFormElement>("form.hcm-form");
        if (form === null) return;
        form.addEventListener("submit", (e) => {
            e.preventDefault();
            void this._handleSubmit();
        });
    }

    private async _handleSubmit(): Promise<void> {
        if (this._submitting) return;

        const pass    = this._shadow.querySelector<HTMLInputElement>("#hcm-pass");
        const confirm = this._shadow.querySelector<HTMLInputElement>("#hcm-confirm");
        const errBox  = this._shadow.querySelector<HTMLElement>(".hcm-error");

        if (pass === null || confirm === null || errBox === null) return;

        this._hideError(errBox);

        const passVal    = pass.value;
        const confirmVal = confirm.value;

        if (passVal.length < 8) {
            this._showError(errBox, "Le mot de passe doit contenir au moins 8 caractères.");
            pass.focus();
            return;
        }
        if (passVal !== confirmVal) {
            this._showError(errBox, "Les mots de passe ne correspondent pas.");
            confirm.classList.add("is-error");
            confirm.focus();
            return;
        }

        const token    = this.getAttribute("token") ?? "";
        const role     = this.getAttribute("role") ?? "";
        const redirect = this.getAttribute("redirect") ?? "";

        if (token === "") {
            this._showError(errBox, "Token de claim manquant — rechargez la page.");
            return;
        }
        if (role === "") {
            this._showError(errBox, "Paramètre rôle manquant — contactez l'émetteur.");
            return;
        }

        const btn = this._shadow.querySelector<HTMLButtonElement>("button.hcm-btn");
        this._submitting = true;
        if (btn !== null) btn.disabled = true;

        try {
            const result = await this._postClaim(token, role, redirect, passVal, confirmVal);
            if (result.ok) {
                this.dispatchEvent(new CustomEvent("hauser-claim:success", {
                    bubbles: true,
                    detail: { response: result.payload },
                }));
                this.remove();
                return;
            }
            this._showError(errBox, result.message ?? "Erreur lors de l'activation.");
            pass.value = "";
            confirm.value = "";
            confirm.classList.remove("is-error");
            pass.focus();
        } catch (err) {
            this._showError(errBox, err instanceof Error ? err.message : "Erreur réseau.");
            this.dispatchEvent(new CustomEvent("hauser-claim:error", {
                bubbles: true,
                detail: { message: (err instanceof Error ? err.message : String(err)) },
            }));
        } finally {
            this._submitting = false;
            if (btn !== null) btn.disabled = false;
        }
    }

    private async _postClaim(
        token: string,
        role: string,
        redirect: string,
        pass: string,
        confirm: string,
    ): Promise<{ ok: boolean; message?: string; payload: unknown }> {
        const body = new URLSearchParams({ formId: "hauser.claim.account", token, role, pass, confirm });
        if (redirect !== "") body.set("redirect", redirect);

        const meta  = document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]');
        const csrf  = meta?.content ?? "";

        const resp    = await fetch("/ha/form", {
            method:  "POST",
            headers: {
                "X-CSRF-Token":     csrf,
                "X-Requested-With": "XMLHttpRequest",
                "Content-Type":     "application/x-www-form-urlencoded",
                "Accept":           "application/json",
            },
            body,
            credentials: "same-origin",
        });

        const payload = await resp.json() as { ok?: boolean; todo?: string; t?: string; msg?: { m?: string }; steps?: unknown[] };

        if (!resp.ok) {
            return { ok: false, message: payload.msg?.m ?? "Erreur serveur.", payload };
        }

        const success = payload.ok === true || payload.todo === "success";
        if (!success) {
            const msg = payload.msg?.m ?? payload.todo ?? "Activation échouée.";
            return { ok: false, message: String(msg), payload };
        }

        return { ok: true, payload };
    }

    private _showError(box: HTMLElement, msg: string): void {
        box.textContent = msg;
        box.hidden = false;
    }

    private _hideError(box: HTMLElement): void {
        box.textContent = "";
        box.hidden = true;
    }
}

if (typeof customElements !== "undefined" && !customElements.get("hauser-claim-modal")) {
    customElements.define("hauser-claim-modal", HauserClaimModal);
}
